import { useEffect, useState } from "react";
import { Music, Plus, Trash2, Pencil, Loader2, GripVertical, ExternalLink, X, Check } from "lucide-react";
import { Faixa } from "@/types/playlist";
import { listarPlaylist, adicionarFaixa, atualizarFaixa, deletarFaixa, extrairVideoId } from "@/lib/playlist";

const inputCls =
  "w-full px-3.5 py-2.5 rounded-xl border border-black/10 bg-black/[0.02] text-sm text-black placeholder-black/25 outline-none focus:border-black focus:bg-white transition";

interface FormState {
  artista: string;
  musica: string;
  linkYoutube: string;
}

const formVazio: FormState = { artista: "", musica: "", linkYoutube: "" };

export default function AdminPlaylist() {
  const [faixas, setFaixas] = useState<Faixa[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [deletando, setDeletando] = useState<string | null>(null);
  const [erro, setErro] = useState("");

  // Modal add/edit
  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState<Faixa | null>(null);
  const [form, setForm] = useState<FormState>(formVazio);

  const carregar = async () => {
    setCarregando(true);
    try {
      setFaixas(await listarPlaylist());
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, []);

  const abrirNova = () => {
    setEditando(null);
    setForm(formVazio);
    setErro("");
    setModalAberto(true);
  };

  const abrirEdicao = (f: Faixa) => {
    setEditando(f);
    setForm({ artista: f.artista, musica: f.musica, linkYoutube: f.linkYoutube });
    setErro("");
    setModalAberto(true);
  };

  const handleSalvar = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro("");
    if (!form.artista.trim() || !form.musica.trim() || !form.linkYoutube.trim()) {
      setErro("Preencha todos os campos."); return;
    }
    if (!extrairVideoId(form.linkYoutube)) {
      setErro("Link do YouTube inválido."); return;
    }
    setSalvando(true);
    try {
      if (editando) {
        await atualizarFaixa(editando.id, {
          artista: form.artista.trim(),
          musica: form.musica.trim(),
          linkYoutube: form.linkYoutube.trim(),
          ordem: editando.ordem,
        });
      } else {
        await adicionarFaixa({
          artista: form.artista.trim(),
          musica: form.musica.trim(),
          linkYoutube: form.linkYoutube.trim(),
          ordem: faixas.length,
        });
      }
      setModalAberto(false);
      await carregar();
    } catch (err) {
      setErro(err instanceof Error ? err.message : "Erro ao salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const handleDeletar = async (id: string) => {
    if (!confirm("Remover esta faixa da playlist?")) return;
    setDeletando(id);
    try {
      await deletarFaixa(id);
      setFaixas((prev) => prev.filter((f) => f.id !== id));
    } finally {
      setDeletando(null);
    }
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold text-black">Playlist</h1>
          <p className="text-sm text-black/40">Músicas exibidas no player da landing page.</p>
        </div>
        <button
          onClick={abrirNova}
          className="flex items-center gap-2 px-4 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition"
        >
          <Plus className="w-4 h-4" />
          Adicionar faixa
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
        {carregando ? (
          <div className="flex items-center justify-center py-16 gap-2 text-black/30 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Carregando...
          </div>
        ) : faixas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-black/30">
            <Music className="w-8 h-8" />
            <p className="text-sm">Nenhuma faixa na playlist.</p>
          </div>
        ) : (
          <ul className="divide-y divide-black/5">
            {faixas.map((f, i) => (
              <li key={f.id} className="flex items-center gap-4 px-5 py-4 hover:bg-black/[0.01] transition">
                <GripVertical className="w-4 h-4 text-black/15 shrink-0" />
                <span className="text-xs text-black/20 w-5 text-right shrink-0">{i + 1}</span>

                {/* Thumbnail */}
                <img
                  src={`https://img.youtube.com/vi/${extrairVideoId(f.linkYoutube)}/default.jpg`}
                  alt=""
                  className="w-12 h-9 object-cover rounded-lg shrink-0 bg-black/5"
                />

                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-black truncate">{f.musica}</p>
                  <p className="text-xs text-black/40 truncate">{f.artista}</p>
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  <a
                    href={f.linkYoutube}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-black/20 hover:text-black hover:bg-black/5 transition"
                    title="Abrir no YouTube"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <button
                    onClick={() => abrirEdicao(f)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-black/20 hover:text-black hover:bg-black/5 transition"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDeletar(f.id)}
                    disabled={deletando === f.id}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-black/20 hover:text-red-500 hover:bg-red-50 transition disabled:opacity-50"
                  >
                    {deletando === f.id
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <Trash2 className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Modal add/edit */}
      {modalAberto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => !salvando && setModalAberto(false)} />
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-black/5">
              <h2 className="text-base font-semibold text-black">
                {editando ? "Editar faixa" : "Nova faixa"}
              </h2>
              {!salvando && (
                <button onClick={() => setModalAberto(false)} className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-black hover:bg-black/5 transition">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <form onSubmit={handleSalvar} className="px-6 py-5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-black/60">Nome da música</label>
                <input
                  required
                  type="text"
                  placeholder="Ex: Blinding Lights"
                  value={form.musica}
                  onChange={(e) => setForm((f) => ({ ...f, musica: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-black/60">Artista</label>
                <input
                  required
                  type="text"
                  placeholder="Ex: The Weeknd"
                  value={form.artista}
                  onChange={(e) => setForm((f) => ({ ...f, artista: e.target.value }))}
                  className={inputCls}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-black/60">Link do YouTube</label>
                <input
                  required
                  type="url"
                  placeholder="https://youtube.com/watch?v=..."
                  value={form.linkYoutube}
                  onChange={(e) => setForm((f) => ({ ...f, linkYoutube: e.target.value }))}
                  className={inputCls}
                />
              </div>

              {erro && (
                <p className="text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">{erro}</p>
              )}

              <div className="flex justify-end gap-2 pt-1">
                {!salvando && (
                  <button type="button" onClick={() => setModalAberto(false)} className="px-4 py-2.5 rounded-xl text-sm font-medium text-black/50 hover:text-black hover:bg-black/5 transition">
                    Cancelar
                  </button>
                )}
                <button
                  type="submit"
                  disabled={salvando}
                  className="flex items-center gap-2 px-5 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-60"
                >
                  {salvando
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</>
                    : <><Check className="w-4 h-4" /> Salvar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
